
// frontend/src/services/SportsDataService.js
import useSWR from 'swr';

const API_BASE = '/api';

const fetcher = async (url) => {

    try {
        const res = await fetch(url);
        if (!res.ok) {
            const errText = await res.text();
            console.error(`[SportsData] Failed: ${res.status} ${res.statusText}`, errText);
            throw new Error(`API Error ${res.status}: ${errText}`);
        }
        const data = await res.json();

        return data;
    } catch (err) {
        console.error("[SportsData] Network/Parse Error:", err);
        throw err;
    }
};

export const useSportsScores = (sport, league) => {
    const { data, error, isLoading } = useSWR(
        `${API_BASE}/sports?sport=${sport}&league=${league}`,
        fetcher,
        {
            refreshInterval: 30000,
            shouldRetryOnError: true
        }
    );

    return {
        games: data?.events || [],
        leagueName: data?.leagues?.[0]?.name || league,
        isLoading,
        isError: error
    };
};

export const useSportsNews = (category) => {
    const { data, error, isLoading } = useSWR(
        `${API_BASE}/news?category=${category}`,
        fetcher
    );

    return {
        news: data || [],
        isLoading,
        isError: error
    };
};

export const useLeagueInfo = (league, type = 'teams') => {
    const { data, error, isLoading } = useSWR(
        `${API_BASE}/league-info?league=${league}&type=${type}`,
        fetcher,
        {
            revalidateOnFocus: false, // Don't spam external API
            revalidateIfStale: false, // Cache aggressively
            dedupingInterval: 3600000 // 1 Hour
        }
    );

    return {
        data: data,
        teams: data?.teams || [],
        isLoading,
        isError: error
    };
};

export const useStandings = (sport, league) => {
    const { data, error, isLoading } = useSWR(
        `${API_BASE}/standings?sport=${sport}&league=${league}`,
        fetcher,
        {
            revalidateOnFocus: false,
            revalidateIfStale: false,
            dedupingInterval: 3600000 // 1 Hour cache
        }
    );

    // Parse ESPN standings format
    let standings = [];
    if (data?.children) {
        // Multi-conference (NBA, NFL) or single table (Soccer)
        data.children.forEach(group => {
            if (group.standings?.entries) {
                standings = [...standings, ...group.standings.entries];
            }
        });
    }

    return {
        standings,
        leagueName: data?.name || league,
        isLoading,
        isError: error
    };
};

// NEW: Team Detail Data Hook (uses SEO-friendly slugs)
export const useTeamData = (teamSlug, leagueSlug) => {
    const { data, error, isLoading } = useSWR(
        teamSlug ? `${API_BASE}/team?teamSlug=${teamSlug}&leagueSlug=${leagueSlug || ''}` : null,
        fetcher,
        {
            revalidateOnFocus: false,
            revalidateIfStale: false,
            dedupingInterval: 3600000 // 1 hour cache
        }
    );

    return {
        team: data?.team || null,
        source: data?.source || 'unknown',
        isLoading,
        isError: error
    };
};
